import axios, { AxiosInstance } from 'axios';
import {
  JiraConfig,
  JiraIssue,
  CreateIssueRequest,
  UpdateIssueRequest,
  SyncToJiraRequest,
  JiraSearchResult,
  JiraConnectionTestResult,
  SessionJiraSync,
  JIRA_CONFIG_KEY,
  JIRA_ENABLED_KEY,
} from '../types/jira';
import { API_V1_URL } from '../config/apiConfig';

// JIRA API クライアント
class JiraApiClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_V1_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // JIRA設定の保存
  saveConfig(config: JiraConfig): void {
    localStorage.setItem(JIRA_CONFIG_KEY, JSON.stringify(config));
  }

  // JIRA設定の取得
  getConfig(): JiraConfig | null {
    const configStr = localStorage.getItem(JIRA_CONFIG_KEY);
    if (!configStr) return null;
    try {
      return JSON.parse(configStr) as JiraConfig;
    } catch (error) {
      console.error('Failed to parse JIRA config:', error);
      return null;
    }
  }

  // JIRA設定の削除
  deleteConfig(): void {
    localStorage.removeItem(JIRA_CONFIG_KEY);
    localStorage.removeItem(JIRA_ENABLED_KEY);
  }

  // JIRA有効/無効状態の保存
  setEnabled(enabled: boolean): void {
    localStorage.setItem(JIRA_ENABLED_KEY, JSON.stringify(enabled));
  }

  // JIRA有効/無効状態の取得
  isEnabled(): boolean {
    const enabledStr = localStorage.getItem(JIRA_ENABLED_KEY);
    if (!enabledStr) return false;
    try {
      return JSON.parse(enabledStr) as boolean;
    } catch (error) {
      return false;
    }
  }

  /**
   * Runs a request and rethrows API errors carrying the server's message,
   * so callers can surface it directly.
   */
  private async request<T>(fallbackMessage: string, send: () => Promise<{ data: T }>): Promise<T> {
    try {
      return (await send()).data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || fallbackMessage);
      }
      throw error;
    }
  }

  // JIRA接続テスト
  async testConnection(config: JiraConfig): Promise<JiraConnectionTestResult> {
    return this.request('Failed to test JIRA connection', () =>
      this.axiosInstance.post<JiraConnectionTestResult>('/jira/test-connection', config)
    );
  }

  // JIRAチケット作成
  async createIssue(request: CreateIssueRequest): Promise<JiraIssue> {
    return this.request('Failed to create JIRA issue', () =>
      this.axiosInstance.post<JiraIssue>('/jira/issues', { ...request, config: this.getConfig() })
    );
  }

  // JIRAチケット取得（設定をボディで送るためPOST）
  async getIssue(issueKey: string): Promise<JiraIssue> {
    return this.request('Failed to get JIRA issue', () =>
      this.axiosInstance.post<JiraIssue>(`/jira/issues/${issueKey}`, this.getConfig())
    );
  }

  // JIRAチケット更新
  async updateIssue(issueKey: string, request: UpdateIssueRequest): Promise<void> {
    await this.request('Failed to update JIRA issue', () =>
      this.axiosInstance.put(`/jira/issues/${issueKey}`, { ...request, config: this.getConfig() })
    );
  }

  // JIRAチケット検索
  async searchIssues(jql: string, maxResults: number = 50): Promise<JiraSearchResult> {
    return this.request('Failed to search JIRA issues', () =>
      this.axiosInstance.post<JiraSearchResult>('/jira/issues/search', {
        config: this.getConfig(),
        jql,
        max_results: maxResults,
      })
    );
  }

  // セッションをJIRAに同期
  async syncSessionToJira(
    sessionId: string,
    request: SyncToJiraRequest
  ): Promise<SessionJiraSync> {
    return this.request('Failed to sync session to JIRA', () =>
      this.axiosInstance.post<SessionJiraSync>(`/sessions/${sessionId}/sync-jira`, {
        ...request,
        config: this.getConfig(),
      })
    );
  }

  // プロジェクトキーからJQLクエリを生成
  generateProjectJql(projectKey: string): string {
    return `project = ${projectKey} ORDER BY created DESC`;
  }

  // テキスト検索用のJQLクエリを生成
  generateSearchJql(projectKey: string, searchText: string): string {
    return `project = ${projectKey} AND text ~ "${searchText}" ORDER BY created DESC`;
  }
}

// シングルトンインスタンス
export const jiraApi = new JiraApiClient();
